import {
  ChecklistRepeatableGroupDef,
  ChecklistSectionDef,
} from "../components/DynamicChecklistManagement/DynamicChecklistManagement";

/** Nature of property options from General Details (Schedule). */
export interface NaturePropertyRow {
  key: string;
  label: string;
  matchValues: string[];
}

export const NATURE_PROPERTY_CATALOG: NaturePropertyRow[] = [
  { key: "agri", label: "Agri", matchValues: ["agri"] },
  {
    key: "existing_building",
    label: "Existing building",
    matchValues: ["existing building"],
  },
  {
    key: "under_construction",
    label: "Under construction",
    matchValues: ["under construction"],
  },
  {
    key: "vacant_land",
    label: "Vacant Land",
    matchValues: ["vacant land"],
  },
  {
    key: "appartment",
    label: "Appartment",
    matchValues: ["appartment", "apartment"],
  },
];

export function surveyGroupIdForNature(naturePropertyKey: string): string {
  return `rg_survey_${naturePropertyKey}`;
}

/** Scoped checklist key: one nature field value per document schedule instance. */
export function surveyNatureFieldScopeKey(
  fdId: string | number,
  scheduleId: string | number,
  fieldId: string
): string {
  return `sv:${String(fdId).trim()}:${String(scheduleId).trim()}:${fieldId}`;
}

export function parseSurveyNatureFieldScopeKey(
  scopedKey: string
): { fdId: string; scheduleId: string; fieldId: string } | null {
  const m = /^sv:([^:]+):([^:]+):(.+)$/.exec(String(scopedKey ?? "").trim());
  if (!m) return null;
  return { fdId: m[1], scheduleId: m[2], fieldId: m[3] };
}

export function naturePropertyLabel(key: string | undefined | null): string {
  const row = NATURE_PROPERTY_CATALOG.find((r) => r.key === key);
  return row?.label ?? String(key ?? "");
}

export function matchNaturePropertyKey(raw: string | undefined | null): string | null {
  const norm = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!norm) return null;
  for (const row of NATURE_PROPERTY_CATALOG) {
    if (row.matchValues.some((v) => v.toLowerCase().replace(/\s+/g, " ") === norm)) {
      return row.key;
    }
  }
  // Loose contains match for common General Detail labels (e.g. "Existing building / flat").
  if (norm.includes("existing") && norm.includes("building")) return "existing_building";
  if (norm.includes("under") && norm.includes("construction")) return "under_construction";
  if (norm.includes("vacant") && norm.includes("land")) return "vacant_land";
  if (norm.includes("agri")) return "agri";
  if (norm.includes("apartment") || norm.includes("appartment")) return "appartment";
  return null;
}

/** Resolve the file's nature-of-property key from front office / scrutiny general detail. */
export function resolveFileNaturePropertyKey(
  frontOfficeRow?: Record<string, string> | null,
  propertyDetails?: Record<string, string> | null
): string | null {
  const candidates = [
    frontOfficeRow?.nature_of_property,
    frontOfficeRow?.natureOfProperty,
    propertyDetails?.natureofProperty,
    propertyDetails?.nature_of_property,
  ];
  for (const raw of candidates) {
    const key = matchNaturePropertyKey(raw == null ? "" : String(raw));
    if (key) return key;
  }
  return null;
}

/** Collect nature keys from General Detail schedules (Document → Schedule). */
export function collectScheduleNaturePropertyKeys(
  generalDetailsByDocument?: Record<string, { schedules?: { nature_doc?: string }[] }> | null,
  activeDocumentFdId?: string | number | null
): Set<string> {
  const keys = new Set<string>();
  if (!generalDetailsByDocument) return keys;
  const fdId = String(activeDocumentFdId ?? "").trim();
  const docKeys = fdId ? [fdId] : Object.keys(generalDetailsByDocument);

  const addFromSchedules = (schedules: { nature_doc?: string }[] | undefined) => {
    (schedules ?? []).forEach((s) => {
      const k = matchNaturePropertyKey(s.nature_doc ?? "");
      if (k) keys.add(k);
    });
  };

  docKeys.forEach((dk) => {
    addFromSchedules(generalDetailsByDocument[dk]?.schedules);
  });

  // Active document may have no schedules under its fd_id; scan all documents as fallback.
  if (!keys.size && fdId) {
    Object.keys(generalDetailsByDocument).forEach((dk) => {
      addFromSchedules(generalDetailsByDocument[dk]?.schedules);
    });
  }

  return keys;
}

/** Merge front-office nature with schedule nature from General Detail (all documents). */
export function resolveSurveyNaturePropertyKeys(
  frontOfficeRow?: Record<string, string> | null,
  propertyDetails?: Record<string, string> | null,
  generalDetailsByDocument?: Record<string, { schedules?: { nature_doc?: string }[] }> | null,
  activeDocumentFdId?: string | number | null
): Set<string> {
  const keys = collectScheduleNaturePropertyKeys(
    generalDetailsByDocument,
    activeDocumentFdId
  );
  const fileKey = resolveFileNaturePropertyKey(frontOfficeRow, propertyDetails);
  if (fileKey) keys.add(fileKey);
  return keys;
}

export function surveyLinkedGroups(section: ChecklistSectionDef): ChecklistRepeatableGroupDef[] {
  return (section.repeatableGroups || []).filter(
    (g) => g.naturePropertyKey != null && g.surveyHierarchyLevel == null
  );
}

export function surveyGroupsVisibleForNatureKeys(
  section: ChecklistSectionDef,
  natureKeys: Set<string>
): ChecklistRepeatableGroupDef[] {
  if (!natureKeys.size) return [];
  return surveyLinkedGroups(section).filter(
    (g) => g.naturePropertyKey != null && natureKeys.has(g.naturePropertyKey)
  );
}

export function surveyConfiguredNatureGroups(
  section: ChecklistSectionDef
): ChecklistRepeatableGroupDef[] {
  return surveyLinkedGroups(section).filter((g) => g.fields.length > 0);
}

/** Nature cards configured in management (flat fields and/or hierarchy levels). */
export function surveyConfiguredNatureCards(
  section: ChecklistSectionDef
): { key: string; label: string; hasFields: boolean }[] {
  const cards = new Map<string, { key: string; label: string; hasFields: boolean }>();
  (section.repeatableGroups || []).forEach((g) => {
    const key = String(g.naturePropertyKey ?? "").trim();
    if (!key) return;
    const prev = cards.get(key);
    const hasFields = (prev?.hasFields ?? false) || (g.fields?.length ?? 0) > 0;
    cards.set(key, {
      key,
      label: g.label || naturePropertyLabel(key),
      hasFields,
    });
  });
  return Array.from(cards.values()).filter((c) => c.hasFields);
}

/** Nature groups to render: matched keys when possible, otherwise all configured groups. */
export function resolveVisibleSurveyNatureGroups(
  section: ChecklistSectionDef,
  natureKeys: Set<string>
): ChecklistRepeatableGroupDef[] {
  const configured = surveyConfiguredNatureGroups(section);
  if (configured.length) {
    if (!natureKeys.size) return configured;
    const matched = configured.filter(
      (g) => g.naturePropertyKey != null && natureKeys.has(g.naturePropertyKey)
    );
    return matched.length ? matched : configured;
  }

  // Hierarchy-only survey sections: synthesize nature cards from scoped hierarchy groups.
  const cards = surveyConfiguredNatureCards(section);
  if (!cards.length) return [];
  const filtered = natureKeys.size
    ? cards.filter((c) => natureKeys.has(c.key))
    : cards;
  const visible = filtered.length ? filtered : cards;
  return visible.map((c) => ({
    id: surveyGroupIdForNature(c.key),
    label: c.label,
    order: 0,
    addButtonLabel: `Add ${c.label}`,
    naturePropertyKey: c.key,
    fields: [],
  }));
}

export function surveyNatureSectionFieldCountForVisibleGroups(
  section: ChecklistSectionDef,
  natureKeys: Set<string>
): number {
  return resolveVisibleSurveyNatureGroups(section, natureKeys).reduce(
    (n, g) => n + g.fields.length,
    0
  );
}

export function surveyGroupsVisibleForFile(
  section: ChecklistSectionDef,
  naturePropertyKey: string | null | undefined
): ChecklistRepeatableGroupDef[] {
  const groups = surveyLinkedGroups(section);
  if (!naturePropertyKey) return [];
  return groups.filter((g) => g.naturePropertyKey === naturePropertyKey);
}

export function surveyNatureSectionFieldCount(
  section: ChecklistSectionDef,
  naturePropertyKey: string | null | undefined
): number {
  return surveyGroupsVisibleForFile(section, naturePropertyKey).reduce(
    (n, g) => n + g.fields.length,
    0
  );
}

/** Flatten survey verification API payload into tag-friendly keys (first row per array). */
export function normalizeSurveyVerificationRow(surveyApi: any): Record<string, string> {
  if (!surveyApi || typeof surveyApi !== "object") return {};
  const out: Record<string, string> = {};
  const assign = (key: string, val: unknown) => {
    if (val != null && typeof val !== "object") out[key] = String(val).trim();
  };
  Object.keys(surveyApi).forEach((k) => {
    const v = (surveyApi as any)[k];
    if (Array.isArray(v) && v.length && typeof v[0] === "object") {
      Object.keys(v[0]).forEach((fk) => assign(fk, v[0][fk]));
    } else {
      assign(k, v);
    }
  });
  return out;
}
